import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineMap,
  HiOutlineXMark,
  HiOutlineEye,
} from "react-icons/hi2";
import { MdAutoAwesome } from "react-icons/md";
import oeeFlowchartService from "../../services/oeeFlowchartService";
import { toast } from "react-toastify";
import SharedModal from "../../components/common/SharedModal";
import PageLoading from "../../components/common/PageLoading";
import "../SafetyObservation.css";

const OEEFlowchartList = () => {
  const navigate = useNavigate();
  const [flowcharts, setFlowcharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadFlowcharts();
  }, []);

  const loadFlowcharts = async () => {
    try {
      setLoading(true);
      const response = await oeeFlowchartService.getAll();
      // Sort flowcharts by ID ascending
      const sortedData = (response || []).sort((a, b) => {
        const idA = a.id || a.Id;
        const idB = b.id || b.Id;
        return idA - idB;
      });
      setFlowcharts(sortedData);
    } catch (err) {
      console.error("Error loading flowcharts:", err);
      toast.error("Failed to load flowcharts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (flowchart) => {
    try {
      await oeeFlowchartService.delete(flowchart.id);
      toast.success("Flowchart deleted successfully.");
      setDeleteConfirm(null);
      loadFlowcharts();
    } catch (err) {
      console.error("Error deleting flowchart:", err);
      toast.error("Failed to delete flowchart. Please try again.");
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return d.toLocaleDateString();
  };

  if (loading) {
    return <PageLoading message="Loading flowcharts..." />;
  }

  return (
    <div>
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">
            <HiOutlineMap className="title-icon" />
            Flowcharts
          </h1>
        </div>
        <div className="header-actions" style={{ display: "flex", gap: "10px" }}>
          <button
            className="btn btn-premium btn-ai"
            onClick={() =>
              navigate("/home/flowchart/new", {
                state: { creationMode: "ai" },
              })
            }
          >
            <span className="ai-icon-wrapper">
              <MdAutoAwesome />
            </span>
            Create with AI
          </button>
          <button
            className="btn btn-primary"
            onClick={() =>
              navigate("/home/flowchart/new", {
                state: { creationMode: "manual" },
              })
            }
          >
            <HiOutlinePlus size={18} />
            Create Manually
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Title</th>
              <th>Description</th>
              <th>Created Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {flowcharts.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  No flowcharts found
                </td>
              </tr>
            ) : (
              flowcharts.map((flowchart, index) => (
                <tr key={flowchart.id}>
                  <td>
                    <button
                      className="link-btn"
                      onClick={() =>
                        navigate(`/home/flowchart/${flowchart.id}`, {
                          state: { flowchartData: flowchart },
                        })
                      }
                    >
                      {index + 1}
                    </button>
                  </td>
                  <td>{flowchart.title || flowchart.Title}</td>
                  <td>
                    {flowchart.description || flowchart.Description || "-"}
                  </td>
                  <td>
                    {formatDate(flowchart.createdAt || flowchart.CreatedAt)}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-view"
                        onClick={() =>
                          navigate(`/home/flowchart/view/${flowchart.id}`, {
                            state: { flowchartData: flowchart },
                          })
                        }
                        title="Preview"
                      >
                        <HiOutlineEye />
                      </button>
                      <button
                        className="btn-icon-edit"
                        onClick={() =>
                          navigate(`/home/flowchart/${flowchart.id}`, {
                            state: { flowchartData: flowchart },
                          })
                        }
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(flowchart)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        size="sm"
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={() => handleDelete(deleteConfirm)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete the flowchart{" "}
            <strong>{deleteConfirm?.title || deleteConfirm?.Title}</strong>? This
            action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default OEEFlowchartList;
